import type { Auth0ContextInterface } from "@auth0/auth0-react";
import { DEFAULT_LOCALE, getLanguageCode, isKnownLocaleTag } from "@pandas/core-ui";
import { canonicalizeRootLocaleUrl, type PartnerRouteContext } from "@pandas/core-ui/route";
import { createRootRouteWithContext, Outlet, redirect } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { platformApi } from "@/services";
import { getAuth0UserLocationId, getAuth0UserPartnerCode } from "@/utils/auth0";
import { fetchPartnerDefaultLocale } from "@/utils/localeResolver";
import { resolvePartnerFromSearch } from "@/utils/resolvePartnerFromSearch";
import { getSearchParam } from "@/utils/searchParams";

export type RouterContext = PartnerRouteContext & {
	auth: Auth0ContextInterface;
};

const showDevtools = false;

const resolveVisionLocale = (legacy: string) => (isKnownLocaleTag(legacy) ? getLanguageCode(legacy) : DEFAULT_LOCALE);

export const Route = createRootRouteWithContext<RouterContext>()({
	beforeLoad: async ({ location, search, context }) => {
		const partnerCode = getSearchParam(search, "partnerCode");
		const locationId = getSearchParam(search, "locationId");
		const partnerToken = getSearchParam(search, "partnerToken");
		const hasUrlParams = partnerCode || locationId || partnerToken;

		const result = await canonicalizeRootLocaleUrl({
			pathname: location.pathname,
			search,
			isLocale: isKnownLocaleTag,
			resolveLocale: resolveVisionLocale,
			reservedPartnerSegments: context.reservedPartnerRouteSegments,
			resolvePartner: async () => {
				try {
					return await resolvePartnerFromSearch({ partnerCode, locationId, partnerToken });
				} catch {
					return null;
				}
			},
		});

		if (result?.kind === "redirect") throw redirect({ href: result.href, replace: true });

		if (result?.kind === "unresolvable") {
			throw redirect({
				to: "/$locale/error",
				params: { locale: DEFAULT_LOCALE },
				search: { code: "partner_not_found" },
				replace: true,
			});
		}

		const canonicalSearch = Object.fromEntries(Object.entries(search).filter(([key]) => key !== "locale"));

		if (location.pathname === "/") {
			if (!hasUrlParams) {
				// No URL params: go directly to Auth0 if not authenticated.
				if (!context.auth.isAuthenticated) {
					context.auth.loginWithRedirect();
					// Return a never-resolving promise so TanStack Router stays in a pending
					// state while Auth0 navigates the browser away.
					return new Promise<never>(() => {});
				}

				// Authenticated: resolve partner from Auth0 user claims.
				let partner = getAuth0UserPartnerCode(context.auth.user);

				if (!partner) {
					const userLocationId = getAuth0UserLocationId(context.auth.user);
					if (userLocationId) {
						try {
							const locationData = await platformApi.getLocationAndPartner(userLocationId);
							partner = locationData.Partner.code;
						} catch {
							// fall through to not-found
						}
					}
				}

				if (!partner) {
					throw redirect({
						to: "/$locale/error",
						params: { locale: DEFAULT_LOCALE },
						search: { code: "partner_not_found" },
						replace: true,
					});
				}

				const locale = await fetchPartnerDefaultLocale(partner).catch(() => DEFAULT_LOCALE);
				throw redirect({
					to: "/$locale/$partner",
					params: { locale, partner },
					search: canonicalSearch,
					replace: true,
				});
			}

			// Has URL params: resolve partner from them.
			const resolvedPartner = await resolvePartnerFromSearch({ partnerCode, locationId, partnerToken });

			if (!resolvedPartner) {
				throw redirect({
					to: "/$locale/error",
					params: { locale: DEFAULT_LOCALE },
					search: { code: "partner_not_found" },
					replace: true,
				});
			}

			let locale = DEFAULT_LOCALE;
			try {
				locale = await fetchPartnerDefaultLocale(resolvedPartner);
			} catch {
				// keep default locale
			}

			throw redirect({
				to: "/$locale/$partner",
				params: { locale, partner: resolvedPartner },
				search: canonicalSearch,
				replace: true,
			});
		}
	},
	component: RootComponent,
});

function RootComponent() {
	return (
		<>
			<Outlet />
			{showDevtools && <TanStackRouterDevtools />}
		</>
	);
}
